import { Feature, GQL } from ".."

export const isOverQuota = (userQuotas: GQL.Quota[] | undefined, feature: Feature) => {
  const quota = getQuota(userQuotas, feature)

  if (!quota) return false
  if (!quota.limit || !quota.used) return false // no limit OR no usage = no quota
  return quota.used >= quota.limit
}

export const getQuota = (userQuotas: GQL.Quota[] | undefined, feature: Feature) => {
  if (!userQuotas || userQuotas.length === 0) return undefined

  const quota = userQuotas.find((q) => q.feature === feature)
  return quota
}

export const getConsumedQuotas = ({
  userQuotas,
  quotasToVerify,
}: {
  userQuotas: GQL.Quota[] | undefined
  quotasToVerify: string[]
}): GQL.Quota[] => {
  if (!userQuotas || userQuotas.length === 0) return []

  const consumedQuotas = quotasToVerify.reduce((consumedQuotas, quotaName) => {
    const isQuotaUsed = isOverQuota(userQuotas, quotaName as Feature)
    if (!isQuotaUsed) return consumedQuotas

    const quota = userQuotas.find((q) => q.feature === quotaName)
    return [...consumedQuotas, quota!]
  }, [] as GQL.Quota[])

  return consumedQuotas
}

interface FeatureAccessWithQuotas {
  hasAccess: boolean
  consumedQuotas: GQL.Quota[]
}

export const verifyFeatureAccessWithQuotas = ({
  featureName,
  billingPlanFeatures,
  quotasToVerify = [],
  userQuotas,
}: {
  featureName: string
  billingPlanFeatures: string[]
  quotasToVerify?: string[]
  userQuotas: GQL.Quota[]
}): FeatureAccessWithQuotas => {
  // verifying if billing plan allows feature
  const isBillingPlanHasFeature = billingPlanFeatures.includes(featureName)
  // verifying user's quotas usage
  const consumedQuotas = getConsumedQuotas({
    userQuotas,
    quotasToVerify: [featureName, ...(quotasToVerify ?? [])],
  })
  const hasConsumedQuotas = consumedQuotas.length > 0

  return {
    hasAccess: isBillingPlanHasFeature && !hasConsumedQuotas,
    consumedQuotas,
  }
}
